<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { DataLayerItem } from '~/composables/useDataLayer'

const {
  data,
  search,
  searchDialogOpen,
  recentSearches,
  loading,
  hasResults,
  categories,
  pages,
  products,
  totalProducts,
  searchRedirect,
} = useQuickSearch()

const router = useRouter()
const { viewItemList } = useDataLayer()
const dataLayerOverrides = computed<Partial<DataLayerItem>>(() => ({ item_list_name: 'quick-search', item_list_id: search.value }))

const setSearch = (newSearch: string) => {
  search.value = newSearch
  searchInput.value?.focus()
  currentFocusIndex.value = -1
}

const openDialog = () => {
  searchDialogOpen.value = true
  search.value = ''
  currentFocusIndex.value = -1

  nextTick(() => {
    searchInput.value?.focus()
  })
}

const closeDialog = () => {
  searchDialogOpen.value = false
  search.value = ''
  currentFocusIndex.value = -1
}

const currentFocusIndex = ref(-1)

const removeFocusVisible = (event: FocusEvent) => {
  (event.target as HTMLElement).classList.remove(':focus-visible')
}

const onKeyPressElement = (event: KeyboardEvent) => {
  if (!searchDialogOpen.value) return

  const resultElements = getInteractiveElements(searchResults.value)

  let focusedElement = null

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (currentFocusIndex.value > 0) {
      currentFocusIndex.value--
    }
    else {
      currentFocusIndex.value = resultElements.length - 1
    }
    focusedElement = resultElements[currentFocusIndex.value]
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (currentFocusIndex.value < resultElements.length - 1) {
      currentFocusIndex.value++
    }
    else {
      console.log('BB')
      currentFocusIndex.value = 0
    }
    focusedElement = resultElements[currentFocusIndex.value]
  }

  if (focusedElement && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
    console.log('C')
    focusedElement.addEventListener('focusout', removeFocusVisible, { once: true })
    focusedElement.classList.add(':focus-visible')
    focusedElement.focus()
  }
}

const onKeyPressWindow = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeDialog()
  }
}

const searchElement = ref<HTMLElement | null>(null)
const searchResults = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)

const doSearch = () => {
  setTimeout(() => {
    searchRedirect()
    closeDialog()
    searchInput.value?.blur()
  }, 600)
}

const highlight = (label: string) => {
  const pattern = new RegExp(search.value, 'gi')
  return label.replaceAll(pattern, `<mark class="search__highlight">${search.value}</mark>`)
}

function getInteractiveElements(container: HTMLElement | null) {
  return Array.from((container || document).querySelectorAll('a[href], button:enabled, input:enabled:not([type="hidden"]), textarea:enabled, select:enabled, label, area[href], object, iframe, [tabindex]:not([tabindex^="-"]), [contenteditable="true"]')) as HTMLElement[]
}

const setFocusOnFirstItem = () => {
  const focusableElements = getInteractiveElements(searchElement.value)
  const resultElements = focusableElements.slice(2, -1)
  if (resultElements.length > 0) {
    currentFocusIndex.value = 0
    resultElements[0]?.focus()
  }
}

const setFocusOnLastItem = () => {
  const focusableElements = getInteractiveElements(searchElement.value)
  const resultElements = focusableElements.slice(2, -1)
  if (resultElements.length > 0) {
    currentFocusIndex.value = resultElements.length - 1
    resultElements[resultElements.length - 1]?.focus()
  }
}

router.beforeEach(() => {
  closeDialog()
})

watch(loading, () => {
  if (!loading.value) {
    // searchDialogOpen.value = true
    if (products.value?.length > 0) {
      viewItemList(products.value, dataLayerOverrides.value)
    }
  }
})

watch(search, () => {
  if (search.value) {
    searchDialogOpen.value = true
  }
  else {
    data.value = null
  }
  currentFocusIndex.value = -1
})

onMounted(() => {
  window.addEventListener('keyup', onKeyPressWindow)
})

onUnmounted(() => {
  window.removeEventListener('keyup', onKeyPressWindow)
})
</script>

<template>
  <div
    ref="searchElement"
    class="search"
    :class="{ 'search--active': searchDialogOpen }"
    :aria-modal="searchDialogOpen ? 'true' : 'false'"
    @keydown="onKeyPressElement"
  >
    
<FormKitLazyProvider>
<button
      type="button"
      class="search__toggle"
      :aria-expanded="searchDialogOpen ? 'true' : 'false'"
      :aria-label="$t('Open search')"
      @click="openDialog"
    >
      <span>{{ search || $t('Searching for ...') }}</span>
      <Icon
        name="icon:search"
        class="icon icon--search"
        aria-hidden="true"
      />
    </button>

    <div
      class="backdrop"
      :class="{ 'backdrop--active': searchDialogOpen }"
      @click="closeDialog()"
    />

    <span
      v-if="searchDialogOpen"
      tabindex="0"
      class="search__focus-trap"
      aria-hidden="true"
      @focus="setFocusOnLastItem"
    />

    <div
      v-if="searchDialogOpen"
      class="search__panel"
      role="dialog"
      aria-labelledby="search__form"
    >
      <div class="search__header">
        <FormKit
          id="search__form"
          name="search__form"
          form-class="search__form"
          type="form"
          :actions="false"
          @submit="doSearch"
        >
          <input
            ref="searchInput"
            v-model="search"
            type="search"
            class="formkit-input"
            data-testid="quicksearch"
            role="combobox"
            aria-describedby="search-help"
            aria-haspopup="listbox"
            :aria-label="$t('Searching for ...')"
            :aria-expanded="searchDialogOpen ? 'true' : 'false'"
            :placeholder="$t('Searching for ...')"
            @focus="searchDialogOpen = true"
          >

          <div class="search__actions">
            <button
              v-if="search.length > 0"
              type="button"
              class="search__reset"
              @click="search = ''"
            >
              <Icon
                name="icon:close"
                class="icon icon--close"
              />
            </button>

            <span
              v-if="search.length > 0"
              class="search__divider"
              aria-hidden="true"
            />

            <button
              :aria-label="$t('Search')"
              type="submit"
              class="search__submit"
            >
              <Icon
                name="icon:search"
                class="icon icon--search"
                aria-hidden="true"
              />
            </button>
          </div>

          <div
            id="search-help"
            class="search__help"
          >
            {{ $t('Search begins after typing 3 characters. Use arrow keys to navigate results.') }}
          </div>
        </FormKit>

        <button
          type="button"
          class="search__close"
          :aria-label="$t('Close')"
          @click="closeDialog()"
        >
          <span class="search__close-label">{{ $t('Close') }}</span>
          <Icon
            name="icon:close"
            class="icon icon--close"
          />
        </button>
      </div>

      <div
        ref="searchResults"
        class="search__results"
      >
        <div
          v-if="search.length > 3 && !hasResults && !loading"
          role="status"
          aria-live="polite"
        >
          {{ $t('Unfortunately we were not able to find any results for your search') }}
        </div>
        <div
          v-else-if="hasResults"
          role="listbox"
        >
          <div
            role="status"
            aria-live="polite"
            class="search__status"
          >
            {{ $t('We found {total} results for your search', { total: categories.length + pages.length + products.length }) }}
            {{ $t('Use arrow keys to navigate results.') }}
          </div>

          <!-- <div
            class="todo search__group search__group--suggestions"
          >
            @todo: autocomplete suggestions
          </div> -->

          <div
            v-if="categories.length > 0"
            class="search__group search__group--categories"
            :aria-label="$t('Categories')"
            role="group"
          >
            <div class="search__heading">
              <span aria-hidden="true">{{ $t('Categories') }}</span>
              <!-- <AppLink
                :to="`/search/?q=${search}`"
                class="search__link-all"
                :aria-label="$t('View all results for {search}', { search })"
              >
                {{ $t('View all') }}
              </AppLink> -->
            </div>
            <ul class="search__list">
              <li
                v-for="category in categories"
                :key="category"
                class="search__item"
              >
                <AppLink
                  :to="`/${category.url}/?q=${search}`"
                  class="search__link"
                  @click="closeDialog"
                >
                  <Icon
                    name="icon:grid"
                    class="icon grid"
                  />
                  <span>
                    <span class="search__label">
                      <UiRichtext :html="highlight(category.label)" />
                    </span>

                    <span
                      v-if="category?.parents?.[0]?.label"
                      class="search__parent"
                    >
                      <UiRichtext :html="' in ' + highlight(category.parents[0].label)" />
                    </span>
                  </span>

                  <Icon
                    name="icon:arrow"
                    class="icon icon--arrow"
                  />
                </AppLink>
              </li>
            </ul>
            <!-- <pre>categories: {{ categories }}</pre> -->
          </div>

          <div
            class="search__group search__group--products"
            :aria-label="$t('Products')"
            role="group"
          >
            <div class="search__heading">
              <span aria-hidden="true">{{ $t('Products') }} ({{ totalProducts }})</span>
              <AppLink
                :to="`/search/?q=${search}`"
                class="search__link-all"
                :aria-label="$t('View all results for {search}', { search })"
              >
                {{ $t('View all') }}
              </AppLink>
            </div>

            <div
              v-if="products.length > 0"
              class="search__products"
            >
              <ProductTile
                v-for="product in products"
                :key="product"
                :data-layer-overrides="dataLayerOverrides"
                :product="product"
                :parameter="`?q=${search}`"
                class="search__products-item"
                list="Search"
              />
            </div>
            <div
              v-else
              class="search__no-results"
            >
              {{ $t('No products found') }}
            </div>

            <!-- <pre>totalProducts: {{ totalProducts }}</pre> -->
            <!-- <pre>products: {{ products }}</pre> -->
          </div>

          <div
            v-if="pages.length > 0"
            class="search__group search__group--pages"
            :aria-label="$t('Pages')"
            role="group"
          >
            <div class="search__heading">
              <span aria-hidden="true">{{ $t('Pages') }}</span>
              <AppLink
                :to="`/search/?q=${search}`"
                class="search__link-all"
                :aria-label="$t('View all results for {search}', { search })"
              >
                {{ $t('View all') }}
              </AppLink>
            </div>

            <ul class="search__list">
              <li
                v-for="page in pages"
                :key="page"
                class="search__item"
              >
                <AppLink
                  :to="`/${page.url}/?q=${search}`"
                  class="search__link"
                  @click="closeDialog"
                >
                  <Icon
                    name="icon:text-file"
                    class="icon icon--text-file"
                  />
                  <span>
                    {{ page.label }}
                    <!-- <span class="search__label">
                      <UiRichtext :html="highlight(page.label)" />
                    </span> -->

                    <!-- <span
                      v-if="page?.parents?.[0]?.label"
                      class="search__parent"
                    >
                      in {{ page.parents[0].label }}
                    </span> -->
                  </span>

                  <Icon
                    name="icon:arrow"
                    class="icon icon--arrow"
                  />
                </AppLink>
              </li>
            </ul>

            <!-- <pre>pages: {{ pages }}</pre> -->
          </div>
        </div>

        <div
          v-else-if="recentSearches.length > 0"
          :aria-label="$t('Recent search terms')"
          role="region"
        >
          <div class="search__group search__group--suggestions">
            <div
              class="search__heading"
              aria-hidden="true"
            >
              {{ $t('Recent search terms') }}
            </div>

            <ul class="search__list">
              <li
                v-for="recentSearch in recentSearches"
                :key="recentSearch"
                class="search__item"
              >
                <button
                  type="button"
                  class="search__link"
                  @click="setSearch(recentSearch)"
                >
                  <Icon
                    name="icon:search"
                    class="icon icon--search"
                  />
                  <span>{{ recentSearch }}</span>
                  <Icon
                    name="icon:arrow"
                    class="icon icon--arrow"
                  />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <span
      v-if="searchDialogOpen"
      tabindex="0"
      class="search__focus-trap"
      aria-hidden="true"
      @focus="setFocusOnFirstItem"
    />
</FormKitLazyProvider>

  </div>
</template>

<style>
@import url("~layers/app/components/App/Search.css");
</style>
